/**
 * Which dashboard this browser was last looking at.
 *
 * The app's front door is a dashboard, not a list of them. Somebody who has
 * built one wants to see their race when they open the app, and a list page in
 * between is a page nobody reads twice.
 *
 * Per browser rather than per account, and deliberately not stored on the
 * server: it is a convenience, it differs between the laptop on the pit wall
 * and the phone in the garage, and syncing it would mean one person opening a
 * different dashboard changed where a colleague landed.
 *
 * Every read and write is guarded. Private windows, cleared site data and
 * browsers set to block storage all make this throw rather than return
 * nothing, and none of them is a reason for the app not to open.
 */
const key = 'racedash.lastDashboard'

export function rememberDashboard(id: string): void {
  try {
    localStorage.setItem(key, id)
  } catch {
    // Not being able to remember is not a failure worth reporting: the app
    // opens on the dashboard list instead, which is where it used to open.
  }
}

export function lastDashboard(): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

/**
 * Called when the remembered dashboard turns out not to open — deleted, or in
 * an organisation this account has left. Without it the front door would point
 * at a dead end every time until somebody cleared their browser storage.
 */
export function forgetDashboard(id: string): void {
  try {
    if (localStorage.getItem(key) === id) localStorage.removeItem(key)
  } catch {
    // Nothing was remembered, so there is nothing to forget.
  }
}
